/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getPatternAnyEntityService } from 'src/luis/core/services/entities/patternAnyEntityService';
import { getEntityHandlerGroupUtils } from 'src/luis/projects/entities/utils/entityHelpers';
import { PatternAnyEntityStore } from 'src/luis/types/stores/entities/PatternAnyEntityStore';

export type PatternAnyEntityHandlerGroup = ReturnType<typeof createPatternAnyEntityHandlerGroup>;

/**
 * Represents a handler group for Pattern.Any operations.
 *
 * @param dependencies The handler group dependencies.
 */
export const createPatternAnyEntityHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const { addOrReplaceEntityStore, getAppVersionStore, getEntityStore } = getEntityHandlerGroupUtils(rootStore);

    const logger = getLogger();

    /**
     * Loads a given Pattern.Any entity.
     *
     * @param id The id of the Pattern.Any entity to load.
     */
    const loadPatternAnyEntity = async ({ context, id }: HandlerParams<Pick<PatternAnyEntityStore, 'id'>>) => {
        const { appId, versionId } = context;
        const patternAnyEntityService = getPatternAnyEntityService({ context });
        const logTimedActivity = logger.createTimedActivityLogger<PatternAnyEntityStore>('LoadPatternAnyEntity', () => ({ entityId: id }));

        const patternAnyEntityStore = await logTimedActivity(patternAnyEntityService.getEntity({ id }));

        addOrReplaceEntityStore({ appId, versionId }, patternAnyEntityStore);
    };

    /**
     * Creates a new Pattern.Any entity.
     *
     * @param name The Pattern.Any entity name.
     */
    const createPatternAnyEntity = async ({ name, context, tracker }: HandlerParams<Pick<PatternAnyEntityStore, 'name'>>) => {
        const { appId, versionId } = context;
        const patternAnyEntityService = getPatternAnyEntityService({ context });
        const logTimedActivity = logger.createTimedActivityLogger<PatternAnyEntityStore>('CreatePatternAnyEntity', (result, error) => {
            const logData = { entityName: name };

            return error ? logData : { ...logData, entityId: result.id };
        });

        const patternAnyEntityStore = await logTimedActivity(patternAnyEntityService.createEntity({ name }));

        getAppVersionStore({ appId, versionId }).entityStoreHouse.add(patternAnyEntityStore);

        if (tracker) {
            tracker.setContext(patternAnyEntityStore);
        }
    };

    /**
     * Deletes a give Pattern.Any entity.
     *
     * @param id The id of the Pattern.Any entity to delete.
     */
    const deletePatternAnyEntity = async ({ context, id, tracker }: HandlerParams<Pick<PatternAnyEntityStore, 'id'>>) => {
        if (tracker) {
            tracker.setContext(id);
        }

        const { appId, versionId } = context;
        const patternAnyEntityService = getPatternAnyEntityService({ context });
        const logTimedActivity = logger.createTimedActivityLogger('DeletePatternAnyEntity', () => ({ entityId: id }));

        await logTimedActivity(patternAnyEntityService.deleteEntity({ id }));

        getAppVersionStore({ appId, versionId }).entityStoreHouse.removeById(id);
    };

    /**
     * Renames a given entity.
     *
     * @param id The id of the Pattern.Any entity to update.
     * @param name The Pattern.Any entity new name.
     */
    const renamePatternAnyEntity = async ({ id: entityId, name, context }: HandlerParams<Pick<PatternAnyEntityStore, 'id' | 'name'>>) => {
        const { appId, versionId } = context;
        const patternAnyEntityService = getPatternAnyEntityService({ context });
        const logTimedActivity = logger.createTimedActivityLogger('RenamePatternAnyEntity', () => ({ entityId }));

        await logTimedActivity(patternAnyEntityService.renameEntity({ id: entityId, name }));

        const entityStore = getEntityStore({ appId, versionId, entityId });
        entityStore.rename(name);
    };

    return {
        loadPatternAnyEntity,
        createPatternAnyEntity,
        deletePatternAnyEntity,
        renamePatternAnyEntity
    };
};
